<template>
  <div class="counselor-card__availability">
    <counselor-card-availability-first-available-text
      v-if="firstAvailableDate"
      :link="link"
      :text="firstAvailableDate"
    />
    <div class="counselor-card__availability-slots">
      <component
        :is="link ? 'a' : 'span'"
        v-for="slots in firstAvailableSlots"
        :key="slots.startTime"
        class="counselor-card__availability-slot"
        :href="link || null"
        >{{ slots.startTime }}</component
      >
      <component
        :is="link ? 'a' : 'span'"
        v-if="priceFromString"
        class="counselor-card__price-from"
        :href="link || null"
        >{{ priceFromString }}</component
      >
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapState } from 'vuex';
import { capitalizeFirstLetter } from 'Library/stringFormatHelpers';
import { useTimeSlots } from 'Main/composables/useTimeSlots';
// @ts-expect-error: Vue SFC, no type declarations available
import CounselorCardAvailabilityFirstAvailableText from './components/CounselorCardAvailabilityFirstAvailableText.vue';
import {
  TSchedule,
  TScheduleItem,
} from 'Services/types/counselor-grid-service-types';
import { TFirstAvailableDateAndSlots } from './availability-slots-types';

export default defineComponent({
  name: 'CounselorCardAvailabilitySlots',

  components: {
    CounselorCardAvailabilityFirstAvailableText,
  },

  setup() {
    const {
      today,
      tomorrow,
      filterTimeSlots,
      generateMeetingTimes,
      formatReadableDate,
    } = useTimeSlots();
    return {
      today,
      tomorrow,
      filterTimeSlots,
      generateMeetingTimes,
      formatReadableDate,
    };
  },

  props: {
    counselor: {
      type: Object as () => Record<string, unknown>,
      default: () => ({}),
    },
    link: {
      type: String,
      default: '',
    },
    schedule: {
      type: Array as () => TSchedule[],
      required: true,
    },
    filteredCompetences: {
      type: Array as () => string[],
      default: () => [],
    },
  },

  data() {
    return {
      currentSite: window.theme_vars.currentSite,
      texts: window.theme_vars.counselorSlider,
    };
  },

  computed: {
    ...mapState('counselorGrid', [
      'competenceAreaItems',
      'filteredAvailableTimes',
    ]),

    firstAvailableDateAndSlots(): TFirstAvailableDateAndSlots | null {
      if (this.firstFilteredAvailableDateAndSlots) {
        return this.firstFilteredAvailableDateAndSlots;
      }

      return this.getFirstAvailableDateAndSlots();
    },

    firstFilteredAvailableDateAndSlots(): TFirstAvailableDateAndSlots | null {
      if (!this.filteredAvailableTimes.length) {
        return null;
      }

      return this.getFirstFilteredAvailableDateAndSlots();
    },

    firstAvailableDate(): string {
      const date = this.firstAvailableDateAndSlots?.date;

      if (!date) {
        return '';
      }

      return capitalizeFirstLetter(this.formatReadableDate(date, false));
    },

    firstAvailableSlots(): TScheduleItem[] {
      const slots = this.firstAvailableDateAndSlots?.slots;

      if (!slots?.length) {
        return [];
      }

      return slots.slice(0, 2);
    },

    filteredCompetenceAreaItems(): Array<{ label: string; slug: string }> {
      const selected = new Set(this.filteredCompetences);
      return this.competenceAreaItems.filter(({ label }) =>
        selected.has(label)
      );
    },

    defaultPrice(): string | undefined {
      const { price_from: priceFrom } = this.counselor || {};

      return priceFrom?.default || undefined;
    },

    competenceAreaPrices(): Record<string, string> | undefined {
      const { price_from: priceFrom } = this.counselor || {};

      return priceFrom?.competence_areas || undefined;
    },

    priceFrom(): string | undefined {
      if (
        !this.filteredCompetenceAreaItems.length ||
        !this.competenceAreaPrices
      ) {
        return this.defaultPrice;
      }

      const matchingCompetence = this.filteredCompetenceAreaItems.find(
        (item: { slug: string }) => this.competenceAreaPrices[item.slug]
      );

      return matchingCompetence
        ? this.competenceAreaPrices[matchingCompetence.slug]
        : this.defaultPrice;
    },

    priceFromString(): string {
      if (!this.priceFrom) {
        return '';
      }

      const string = `${this.texts.priceFromText} ${this.priceFrom}`;

      return this.firstAvailableDate ? string : capitalizeFirstLetter(string);
    },
  },

  methods: {
    getFirstFilteredAvailableDateAndSlots(): TFirstAvailableDateAndSlots | null {
      if (
        !this.filteredAvailableTimes?.length ||
        this.filteredAvailableTimes.length > 1 ||
        !this.counselor?.available_times ||
        !this.counselor.available_times[this.filteredAvailableTimes[0]]
      ) {
        return null;
      }

      const { date, slots } =
        this.counselor.available_times[this.filteredAvailableTimes[0]];

      return {
        date,
        slots,
      };
    },

    getFirstAvailableDateAndSlots(): TFirstAvailableDateAndSlots | null {
      const firstAvailableSlot = this.getFirstAvailableSlot();

      if (!firstAvailableSlot) {
        return null;
      }

      return {
        date: firstAvailableSlot.date,
        slots: this.generateMeetingTimes(
          [firstAvailableSlot.availableSlot],
          firstAvailableSlot.date,
          45
        ),
      };
    },

    getFirstAvailableSlot(): {
      date: string;
      availableSlot: TScheduleItem;
    } | null {
      // Decouple array from props
      const slots = [...this.schedule]
        .filter(({ isActive }) => isActive)
        .map(
          (
            day: TSchedule & {
              schedule:
                | TScheduleItem[]
                | { all?: TScheduleItem[]; phone?: TScheduleItem[] };
            }
          ) => {
            return {
              ...day,
              schedule:
                (day.schedule as { phone?: TScheduleItem[] }).phone || [],
            };
          }
        );

      const filteredSlots = slots.reduce(
        (
          timeslots: Array<{ schedule: TScheduleItem[]; date: string }>,
          day: { schedule: TScheduleItem[]; date: string }
        ) => this.filterTimeSlots(timeslots, day, 5),
        [] as Array<{ schedule: TScheduleItem[]; date: string }>
      );

      for (const { schedule, date } of filteredSlots) {
        for (const slot of schedule as TScheduleItem[]) {
          if (
            !slot.available ||
            slot.inactive ||
            (slot.length !== undefined && slot.length < 45)
          ) {
            continue;
          }

          const meetingTimes = this.generateMeetingTimes([slot], date, 45);

          if (meetingTimes.length > 0) {
            return { date, availableSlot: slot };
          }
        }
      }

      return null;
    },
  },
});
</script>
